import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUploadField from "@/components/admin/ImageUploadField";

const EMPTY = {
  name: "", sku: "", category: "", series: "", price: 0,
  short_description: "", description: "", image_url: "",
  featured: false, in_stock: true, specs: [], gallery: [],
};

const inputCls = "h-11 w-full border border-black/20 bg-white px-3 text-sm outline-none focus:border-gavra";
const labelCls = "font-mono-hw text-[11px] uppercase tracking-widest text-black/50";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});

  const load = () => api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setErrors({}); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...EMPTY, ...p, specs: p.specs || [], gallery: p.gallery || [] });
    setErrors({});
    setOpen(true);
  };

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const setSpec = (i, key) => (e) =>
    setForm((f) => ({ ...f, specs: f.specs.map((s, si) => (si === i ? { ...s, [key]: e.target.value } : s)) }));
  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { label: "", value: "" }] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, si) => si !== i) }));

  const setGallery = (i) => (e) =>
    setForm((f) => ({ ...f, gallery: f.gallery.map((g, gi) => (gi === i ? e.target.value : g)) }));
  const setGalleryVal = (i, val) =>
    setForm((f) => ({ ...f, gallery: f.gallery.map((g, gi) => (gi === i ? val : g)) }));
  const addGallery = () => setForm((f) => ({ ...f, gallery: [...f.gallery, ""] }));
  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, gi) => gi !== i) }));

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name?.trim()) errs.name = "Product name is required.";
    if (!form.sku?.trim()) errs.sku = "SKU is required.";
    if (!form.category?.trim()) errs.category = "Category is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fill in the required fields.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      specs: form.specs.filter((s) => s.label && s.value),
      gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Product deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-hw text-[11px] uppercase tracking-[0.25em] text-gavra">Catalogue</p>
          <h1 className="mt-2 font-heading text-4xl font-black uppercase tracking-tight md:text-5xl">Products</h1>
        </div>
        <button data-testid="add-product-btn" onClick={openNew} className="flex items-center gap-2 bg-black px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra">
          <Plus className="h-4 w-4" /> Add product
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-black/10">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-black/10 bg-secondary">
            <tr className="font-mono-hw text-[10px] uppercase tracking-widest text-black/50">
              <th className="p-4">Product</th>
              <th className="p-4">SKU</th>
              <th className="p-4">Category</th>
              <th className="p-4">Price</th>
              <th className="p-4">Featured</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {products.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.sku}`} className="hover:bg-secondary/50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 border border-black/10 overflow-hidden bg-secondary">
                      {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <span className="font-heading font-bold">{p.name}</span>
                  </div>
                </td>
                <td className="p-4 font-mono-hw text-xs">{p.sku}</td>
                <td className="p-4 text-sm">{p.category}</td>
                <td className="p-4 font-heading font-bold">${p.price?.toFixed(2)}</td>
                <td className="p-4">
                  {p.featured ? <span className="bg-gavra px-2 py-0.5 text-[10px] font-bold uppercase text-white">Yes</span> : <span className="text-xs text-black/30">—</span>}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-product-${p.sku}`} onClick={() => openEdit(p)} className="border border-black/20 p-2 hover:border-gavra hover:text-gavra">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-product-${p.sku}`} onClick={() => setDeleteId(p.id)} className="border border-black/20 p-2 hover:border-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-sm text-black/40">No products yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none border-black/10" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tight">
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Name *</label>
                <input data-testid="pf-name" value={form.name} onChange={set("name")} className={`mt-1.5 ${inputCls} ${errors.name ? "border-destructive" : ""}`} />
                {errors.name && <p data-testid="pf-name-error" className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>
              <div>
                <label className={labelCls}>SKU *</label>
                <input data-testid="pf-sku" value={form.sku} onChange={set("sku")} className={`mt-1.5 ${inputCls} ${errors.sku ? "border-destructive" : ""}`} placeholder="GV-XX-0000" />
                {errors.sku && <p data-testid="pf-sku-error" className="mt-1 text-xs text-destructive">{errors.sku}</p>}
              </div>
              <div>
                <label className={labelCls}>Category *</label>
                <input data-testid="pf-category" value={form.category} onChange={set("category")} className={`mt-1.5 ${inputCls} ${errors.category ? "border-destructive" : ""}`} placeholder="Drum Hoops" />
                {errors.category && <p data-testid="pf-category-error" className="mt-1 text-xs text-destructive">{errors.category}</p>}
              </div>
              <div>
                <label className={labelCls}>Series</label>
                <input data-testid="pf-series" value={form.series} onChange={set("series")} className={`mt-1.5 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Price (USD)</label>
                <input data-testid="pf-price" type="number" step="0.01" value={form.price} onChange={set("price")} className={`mt-1.5 ${inputCls}`} />
              </div>
              <div className="sm:col-span-2">
                <ImageUploadField label="Main image" testid="pf-image" value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Gallery images (shown on product detail)</label>
                  <button type="button" onClick={addGallery} className="flex items-center gap-1 text-[11px] font-bold uppercase text-gavra">
                    <Plus className="h-3 w-3" /> Add image
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.gallery.map((g, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1">
                        <ImageUploadField compact testid={`pf-gallery-${i}`} value={g} onChange={(v) => setGalleryVal(i, v)} />
                      </div>
                      <button type="button" onClick={() => removeGallery(i)} className="mt-1.5 border border-black/20 px-3 py-2.5 hover:border-destructive hover:text-destructive"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {form.gallery.length === 0 && (
                    <p className="text-xs text-black/40">No gallery images yet — the main image will be used.</p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Short description</label>
                <input data-testid="pf-short" value={form.short_description} onChange={set("short_description")} className={`mt-1.5 ${inputCls}`} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Description</label>
                <textarea data-testid="pf-desc" value={form.description} onChange={set("description")} rows={4} className="mt-1.5 w-full border border-black/20 bg-white p-3 text-sm outline-none focus:border-gavra" />
              </div>
            </div>

            {/* specs */}
            <div className="border-t border-black/10 pt-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Specifications</label>
                <button type="button" onClick={addSpec} className="flex items-center gap-1 text-[11px] font-bold uppercase text-gavra">
                  <Plus className="h-3 w-3" /> Add spec
                </button>
              </div>
              <div className="mt-3 space-y-2">
                {form.specs.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={s.label} onChange={setSpec(i, "label")} placeholder="Label" className={`${inputCls} h-10`} />
                    <input value={s.value} onChange={setSpec(i, "value")} placeholder="Value" className={`${inputCls} h-10`} />
                    <button type="button" onClick={() => removeSpec(i)} className="border border-black/20 px-3 hover:border-destructive hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-6 border-t border-black/10 pt-4">
              <label className="flex items-center gap-2 text-sm font-bold">
                <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#FF5A00]" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm font-bold">
                <input data-testid="pf-instock" type="checkbox" checked={form.in_stock} onChange={set("in_stock")} className="h-4 w-4 accent-[#FF5A00]" /> In stock
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="border border-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] hover:bg-black hover:text-white">Cancel</button>
              <button data-testid="pf-save" type="submit" disabled={saving} className="bg-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50">
                {saving ? "Saving…" : "Save product"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none border-black/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase">Delete product?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product" onClick={confirmDelete} className="rounded-none bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
